import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, FileText, AlertCircle, CheckCircle2, Download } from "lucide-react";

/**
 * Bulk-import products from a CSV file. Talks to the admin endpoints under
 * /api/admin/products/import-csv (+ /template). Supports a dry-run preview
 * before applying the import.
 */
export default function ProductImportDialog({ open, onOpenChange, onImported }) {
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(null); // { would_create, would_update, errors }
    const [result, setResult] = useState(null); // { created, updated, errors }
    const inputRef = useRef(null);

    const reset = () => {
        setFile(null);
        setPreview(null);
        setResult(null);
        if (inputRef.current) inputRef.current.value = "";
    };

    const handleOpenChange = (v) => {
        if (!v) reset();
        onOpenChange(v);
    };

    const downloadTemplate = async () => {
        try {
            const { data } = await api.get("/admin/products/import-csv/template", {
                responseType: "blob",
            });
            const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "products-import-template.csv";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    const upload = async (dryRun) => {
        if (!file) {
            toast.error("Choose a CSV file first");
            return;
        }
        setBusy(true);
        if (dryRun) setPreview(null); else setResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(
                `/admin/products/import-csv?dry_run=${dryRun ? "true" : "false"}`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            if (dryRun) {
                setPreview(data);
                if (data.errors?.length) {
                    toast.warning(`Preview ready — ${data.errors.length} row(s) had errors`);
                } else {
                    toast.success(
                        `Ready to import — ${data.would_create} new, ${data.would_update} updates`,
                    );
                }
            } else {
                setResult(data);
                const totalOk = (data.created || 0) + (data.updated || 0);
                if (totalOk > 0) {
                    toast.success(
                        `Imported — created ${data.created}, updated ${data.updated}` +
                            (data.errors?.length ? ` (${data.errors.length} skipped)` : ""),
                    );
                    onImported?.();
                } else if (data.errors?.length) {
                    toast.error(`Import failed — ${data.errors.length} row(s) had errors`);
                }
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-xl rounded-3xl" data-testid="product-import-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Import products from CSV</DialogTitle>
                </DialogHeader>

                <div className="space-y-4 text-sm">
                    <div className="rounded-2xl bg-paper-alt p-4">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <p className="font-medium text-ink-900">1. Start with the template</p>
                                <p className="text-xs text-ink-700 mt-1">
                                    Download the sample CSV — headers match every product field including the new
                                    specification columns. Use semicolons (<code>;</code>) inside the
                                    <code className="mx-1">tags</code> and
                                    <code className="mx-1">gallery</code> columns to separate multiple values.
                                </p>
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={downloadTemplate}
                                className="rounded-full shrink-0"
                                data-testid="product-import-template-btn"
                            >
                                <Download className="h-4 w-4 mr-1.5" /> Template
                            </Button>
                        </div>
                    </div>

                    <div className="rounded-2xl bg-paper-alt p-4">
                        <p className="font-medium text-ink-900">2. Upload your CSV</p>
                        <p className="text-xs text-ink-700 mt-1 mb-3">
                            Rows are matched to existing products by <code>id</code> column (if provided) or by
                            product name — matches are updated, the rest are created.
                        </p>
                        <input
                            ref={inputRef}
                            type="file"
                            accept=".csv,text/csv"
                            onChange={(e) => {
                                setFile(e.target.files?.[0] || null);
                                setPreview(null);
                                setResult(null);
                            }}
                            className="block w-full text-sm file:mr-3 file:rounded-full file:border-0 file:bg-brand-500 file:text-white file:px-4 file:py-2 file:font-medium hover:file:bg-brand-600"
                            data-testid="product-import-file"
                        />
                        {file && (
                            <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-ink-700">
                                <FileText className="h-3.5 w-3.5" /> {file.name} · {(file.size / 1024).toFixed(1)} KB
                            </p>
                        )}
                    </div>

                    {preview && (
                        <div className="rounded-2xl border border-brand-100 bg-brand-50/50 p-4" data-testid="product-import-preview">
                            <p className="font-medium text-ink-900 inline-flex items-center gap-1.5">
                                <CheckCircle2 className="h-4 w-4 text-brand-600" /> Preview
                            </p>
                            <ul className="text-xs text-ink-700 mt-2 space-y-1">
                                <li>To create: <strong className="text-ink-900">{preview.would_create}</strong></li>
                                <li>To update: <strong className="text-ink-900">{preview.would_update}</strong></li>
                                <li>Errors: <strong className="text-ink-900">{preview.errors?.length || 0}</strong></li>
                            </ul>
                            {preview.errors?.length > 0 && (
                                <details className="mt-3 text-xs">
                                    <summary className="cursor-pointer text-accent-700">Show errors</summary>
                                    <ul className="mt-2 list-disc pl-5 text-ink-700 space-y-0.5 max-h-40 overflow-auto">
                                        {preview.errors.map((e, i) => <li key={i}>{e}</li>)}
                                    </ul>
                                </details>
                            )}
                        </div>
                    )}

                    {result && (
                        <div
                            className={`rounded-2xl p-4 ${result.created + result.updated > 0 ? "border border-emerald-200 bg-emerald-50" : "border border-accent-200 bg-accent-50"}`}
                            data-testid="product-import-result"
                        >
                            <p className="font-medium text-ink-900 inline-flex items-center gap-1.5">
                                {result.errors?.length && (result.created + result.updated) === 0
                                    ? <AlertCircle className="h-4 w-4 text-accent-600" />
                                    : <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                                Import complete
                            </p>
                            <ul className="text-xs text-ink-700 mt-2 space-y-1">
                                <li>Created: <strong className="text-ink-900">{result.created}</strong></li>
                                <li>Updated: <strong className="text-ink-900">{result.updated}</strong></li>
                                <li>Skipped: <strong className="text-ink-900">{result.errors?.length || 0}</strong></li>
                            </ul>
                            {result.errors?.length > 0 && (
                                <details className="mt-3 text-xs">
                                    <summary className="cursor-pointer text-accent-700">Show skipped rows</summary>
                                    <ul className="mt-2 list-disc pl-5 text-ink-700 space-y-0.5 max-h-40 overflow-auto">
                                        {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                                    </ul>
                                </details>
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleOpenChange(false)}
                        className="rounded-full"
                    >
                        Close
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        disabled={!file || busy}
                        onClick={() => upload(true)}
                        className="rounded-full"
                        data-testid="product-import-preview-btn"
                    >
                        {busy && preview === null ? "Validating…" : "Preview"}
                    </Button>
                    <Button
                        type="button"
                        disabled={!file || busy}
                        onClick={() => upload(false)}
                        className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta"
                        data-testid="product-import-submit-btn"
                    >
                        <Upload className="h-4 w-4 mr-1.5" />
                        {busy && result === null ? "Importing…" : "Import"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
